/**
 * LLM provider pricing table + cost calculation.
 *
 * Hardcoded with periodic refresh — see PRICING_AS_OF below. When provider
 * pricing changes, update the table and bump PRICING_AS_OF. The corresponding
 * pricing data lives backend-side in the litellm_* schema for av_native; this
 * mirrors the active subset for harness in-process cost calculation.
 *
 * Plaintext stays in the customer's process. Only the calculated cost (a number)
 * crosses the wire to AgentVault as span metadata.
 *
 * Tables are keyed by `LlmProvider` (matching the ones detected in
 * llm-response-parser.ts) so models that exist on multiple providers with
 * different pricing — e.g. DeepSeek-direct vs DeepSeek-via-OpenRouter — get
 * correctly priced based on which API the call hit.
 */

import type { LlmProvider } from "./llm-response-parser.js";
import { OPENROUTER_PRICING, OPENROUTER_PRICING_AS_OF } from "./llm-pricing-openrouter.js";

/**
 * Date of the OpenRouter catalogue the Anthropic, OpenAI and OpenRouter tables
 * come from. Refresh: `node scripts/refresh-pricing.mjs` (rewrites
 * llm-pricing-openrouter.ts). Hand-kept entries below only fill gaps for models
 * OpenRouter no longer lists, plus DeepSeek-direct, which OpenRouter can't price.
 */
export const PRICING_AS_OF = OPENROUTER_PRICING_AS_OF;

interface ModelPricing {
  /** USD per million input tokens */
  inputPerMtoken: number;
  /** USD per million output tokens */
  outputPerMtoken: number;
}

/** Cross-provider models (used when no provider is supplied or a provider table misses). */
const SHARED_PRICING: Readonly<Record<string, ModelPricing>> = {
  // Free-tier marker (preserves cost=0 path through buildLlmSpan)
  "free-tier-model": { inputPerMtoken: 0, outputPerMtoken: 0 },
};

/**
 * Provider-keyed pricing tables. Lookup order in calculateCost():
 *   1. PRICING[provider][normalized]          ← exact match
 *   2. PRICING[provider] longest-prefix       ← dated suffixes etc.
 *   3. SHARED_PRICING[normalized]             ← cross-provider models
 *   4. legacy cross-table search              ← back-compat for unknown provider
 *
 * Keys are post-OpenRouter-prefix-strip canonical names (the parser strips
 * `qwen/` / `x-ai/` / `deepseek/` etc. before lookup runs).
 */
const LEGACY_PRICING: Readonly<Record<LlmProvider, Record<string, ModelPricing>>> = {
  // Superseded by OPENROUTER_PRICING wherever OpenRouter lists the model.
  anthropic: {
    "claude-opus-4-7": { inputPerMtoken: 15, outputPerMtoken: 75 },
    "claude-opus-4": { inputPerMtoken: 15, outputPerMtoken: 75 },
    "claude-sonnet-4-6": { inputPerMtoken: 3, outputPerMtoken: 15 },
    "claude-sonnet-4": { inputPerMtoken: 3, outputPerMtoken: 15 },
    "claude-haiku-4-5": { inputPerMtoken: 0.8, outputPerMtoken: 4 },
    "claude-3-5-sonnet": { inputPerMtoken: 3, outputPerMtoken: 15 },
    "claude-3-5-haiku": { inputPerMtoken: 0.8, outputPerMtoken: 4 },
    "claude-3-opus": { inputPerMtoken: 15, outputPerMtoken: 75 },
    "claude-3-sonnet": { inputPerMtoken: 3, outputPerMtoken: 15 },
    "claude-3-haiku": { inputPerMtoken: 0.25, outputPerMtoken: 1.25 },
  },

  openai: {
    "gpt-4o": { inputPerMtoken: 2.5, outputPerMtoken: 10 },
    "gpt-4o-mini": { inputPerMtoken: 0.15, outputPerMtoken: 0.6 },
    "gpt-4-turbo": { inputPerMtoken: 10, outputPerMtoken: 30 },
    "gpt-4": { inputPerMtoken: 30, outputPerMtoken: 60 },
    "gpt-3.5-turbo": { inputPerMtoken: 0.5, outputPerMtoken: 1.5 },
    "o1-preview": { inputPerMtoken: 15, outputPerMtoken: 60 },
    "o1-mini": { inputPerMtoken: 3, outputPerMtoken: 12 },
    "o3-mini": { inputPerMtoken: 1.1, outputPerMtoken: 4.4 },
  },

  openrouter: {
    // Qwen via OpenRouter — keys are post-prefix-strip canonical names.
    "qwen3.5-397b-a17b": { inputPerMtoken: 0.39, outputPerMtoken: 2.34 },
    "qwen3.6-plus": { inputPerMtoken: 0.26, outputPerMtoken: 1.56 },
    "qwen3-max-thinking": { inputPerMtoken: 0.001, outputPerMtoken: 0.004 },
    "qwen3-coder:free": { inputPerMtoken: 0, outputPerMtoken: 0 },

    // Grok via OpenRouter
    "grok-4.20-multi-agent": { inputPerMtoken: 2, outputPerMtoken: 2 },

    // DeepSeek routed through OpenRouter (cheaper than direct on output tokens).
    "deepseek-v3.2": { inputPerMtoken: 0.28, outputPerMtoken: 0.42 },
    "deepseek-v3.2-speciale": { inputPerMtoken: 0.4, outputPerMtoken: 1.2 },
    "deepseek-v4-pro": { inputPerMtoken: 0.28, outputPerMtoken: 0.42 },
    "deepseek-v4-flash": { inputPerMtoken: 0.14, outputPerMtoken: 0.28 },
  },

  // DeepSeek direct (deepseek.com) — different pricing than via-OR.
  // Output prices are noticeably higher on the direct API.
  deepseek: {
    "deepseek-v3.2": { inputPerMtoken: 0.27, outputPerMtoken: 1.1 },
    "deepseek-v3.2-speciale": { inputPerMtoken: 0.55, outputPerMtoken: 2.19 },
    "deepseek-v4-pro": { inputPerMtoken: 0.27, outputPerMtoken: 1.1 },
    "deepseek-v4-flash": { inputPerMtoken: 0.14, outputPerMtoken: 0.55 },
  },

  // Empty for now — add as we onboard customers using these providers.
  groq: {},
  together: {},
  mistral: {},
  azure: {},
};

/** OpenRouter's catalogue wins; legacy entries survive only where it is silent. */
const PRICING: Readonly<Record<LlmProvider, Record<string, ModelPricing>>> = {
  ...LEGACY_PRICING,
  anthropic: { ...LEGACY_PRICING.anthropic, ...OPENROUTER_PRICING.anthropic },
  openai: { ...LEGACY_PRICING.openai, ...OPENROUTER_PRICING.openai },
  openrouter: { ...LEGACY_PRICING.openrouter, ...OPENROUTER_PRICING.openrouter },
};

/**
 * Strip provider-dated suffixes from a model name to match the canonical form.
 * Anthropic uses `-YYYYMMDD`, OpenAI uses `-YYYY-MM-DD`. Both collapse to the
 * un-suffixed key in PRICING.
 */
export function normalizeModelName(model: string): string {
  return model
    .replace(/-\d{4}-\d{2}-\d{2}$/, "") // OpenAI: -2024-08-06
    .replace(/-\d{8}$/, ""); // Anthropic: -20241022
}

function lookupInTable(
  table: Readonly<Record<string, ModelPricing>>,
  normalized: string,
): ModelPricing | undefined {
  const exact = table[normalized];
  if (exact) return exact;

  // Longest matching prefix (handles unstripped variants).
  let bestKey: string | null = null;
  for (const key of Object.keys(table)) {
    if (
      normalized.startsWith(key) &&
      (bestKey === null || key.length > bestKey.length)
    ) {
      bestKey = key;
    }
  }
  return bestKey ? table[bestKey] : undefined;
}

/**
 * Compute USD cost for an LLM call.
 *
 * Returns `undefined` (not 0) when the model isn't in any pricing table —
 * intentional: an unknown model's cost should appear absent in dashboards
 * rather than misleadingly free.
 *
 * If `provider` is supplied, that provider's table is preferred so a model
 * priced differently across providers (e.g. DeepSeek direct vs via OpenRouter)
 * gets the correct number. With `provider` omitted, the function searches all
 * tables — back-compat for callers that don't yet know the provider.
 */
export function calculateCost(
  model: string,
  inputTokens: number,
  outputTokens: number,
  provider?: LlmProvider,
): number | undefined {
  if (!model) return undefined;
  const normalized = normalizeModelName(model);

  let pricing: ModelPricing | undefined;

  // 1. Provider-specific table (preferred when supplied).
  if (provider && PRICING[provider]) {
    pricing = lookupInTable(PRICING[provider], normalized);
  }

  // 2. Cross-provider shared table.
  if (!pricing) {
    pricing = lookupInTable(SHARED_PRICING, normalized);
  }

  // 3. Legacy fallback: search every provider's table. Used when the caller
  //    didn't provide a provider, or the model isn't in the named provider's
  //    table (e.g. a misclassified Anthropic-via-OpenRouter call).
  if (!pricing) {
    for (const key of Object.keys(PRICING) as LlmProvider[]) {
      if (provider && key === provider) continue; // already tried
      pricing = lookupInTable(PRICING[key], normalized);
      if (pricing) break;
    }
  }

  if (!pricing) return undefined;

  const inputCost = (inputTokens / 1_000_000) * pricing.inputPerMtoken;
  const outputCost = (outputTokens / 1_000_000) * pricing.outputPerMtoken;
  return inputCost + outputCost;
}
